import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import Typography from "components/basic/typography";

import { ConversationView } from "../../types";

interface ConversationPreviewProps {
  conversation: ConversationView;
  selected: boolean;
  onClick: () => void;
  onToggleStar: () => void;
  onToggleArchive: () => void;
}

const ConversationPreview: React.FC<ConversationPreviewProps> = ({
  conversation,
  selected,
  onClick,
  onToggleStar,
  onToggleArchive,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("messages");
  const format = useFormat();
  const [hovered, setHovered] = useState(false);
  // Hover never fires on touch, so below md the row actions are always shown
  // rather than being unreachable.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const { otherPartyName, otherPartyAvatarUrl, lastMessageBody, unreadCount } =
    conversation;

  const stop = (handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      px={theme.spacing(2)}
      py={theme.spacing(1)}
      borderBottom={`1px solid ${theme.palette.primary.main}`}
      sx={{
        cursor: "pointer",
        height: { xs: 76, md: 100 },
        backgroundColor: selected
          ? theme.palette.action.selected
          : hovered
            ? theme.palette.action.hover
            : "transparent",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Badge
        color="primary"
        badgeContent={unreadCount > 0 ? unreadCount : null}
        overlap="circular"
      >
        <Avatar
          src={otherPartyAvatarUrl ?? undefined}
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{
            width: { xs: 48, md: 70 },
            height: { xs: 48, md: 70 },
            flexShrink: 0,
          }}
        />
      </Badge>

      <Box
        ml={{ xs: theme.spacing(2), md: theme.spacing(3) }}
        flexGrow={1}
        minWidth={0}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={theme.spacing(1)}
        >
          <Typography
            variant="h4"
            noWrap
            style={{ fontWeight: "bold" }}
            sx={{ minWidth: 0 }}
          >
            {otherPartyName}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            gap={theme.spacing(0.5)}
            flexShrink={0}
          >
            {(hovered || isCompact || conversation.starred) && (
              <IconButton
                size="small"
                onClick={stop(onToggleStar)}
                aria-label={t(
                  conversation.starred
                    ? "conversation.unstar"
                    : "conversation.star",
                )}
              >
                {conversation.starred ? (
                  <StarIcon
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
            )}
            {(hovered || isCompact) && (
              <IconButton
                size="small"
                onClick={stop(onToggleArchive)}
                aria-label={t(
                  conversation.archived
                    ? "conversation.unarchive"
                    : "conversation.archive",
                )}
              >
                {conversation.archived ? (
                  <UnarchiveOutlinedIcon fontSize="small" />
                ) : (
                  <ArchiveOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            )}
            <Typography variant="subtitle1">
              {format.timestamp(conversation.lastMessageAt)}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="subtitle1"
          style={{
            fontWeight: unreadCount > 0 ? "bold" : "normal",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {lastMessageBody ?? t("conversation.no_messages")}
        </Typography>
      </Box>
    </Box>
  );
};

export default ConversationPreview;
